import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import {
  addDays,
  differenceInDays,
  format,
  isToday,
  isTomorrow,
} from "date-fns";
import { useMemo, useState } from "react";

import { isEqual } from "lodash";

export interface DateSelectorProps {
  noOfDays?: number;
  onDateChange: (date: Date) => void;
  datesContainer?: FlexProps;
  dateBox?: BoxProps;
  startDate?: Date;
  currentDate?: Date;
  endDate?: Date;
  timeDisplayType?: "single" | "multiple";
  borderType?: "bottom" | "circle";
  selectedBackgroundColor?: string;
  selectedTextColor?: string;
  selectedBorderColor?: string;
  showWeekDay?: boolean;
  weekDayProps?: TextProps;
}

function DateSelector({
  noOfDays = 4,
  onDateChange,
  currentDate,
  startDate,
  datesContainer,
  dateBox,
  endDate,
  timeDisplayType,
  borderType,
  selectedBackgroundColor = "transparent",
  selectedTextColor,
  selectedBorderColor,
  showWeekDay = false,
  weekDayProps,
}: DateSelectorProps) {
  const [selectedDate, setSelectedDate] = useState(startDate || new Date());
  const dates = useMemo(() => {
    let numberOfDays = noOfDays;

    if (endDate) {
      if (startDate) {
        numberOfDays = differenceInDays(endDate, startDate);
      } else {
        numberOfDays = differenceInDays(endDate, new Date());
      }
    }

    const dateArr: Date[] = [];
    for (let i = 0; i <= numberOfDays; i++) {
      const date = startDate || new Date();

      dateArr.push(new Date(addDays(date, i)));
    }
    return dateArr;
  }, [noOfDays, startDate, endDate]);

  return (
    <Flex alignItems="center" gap={3} {...datesContainer} overflow={"auto"}>
      {dates.map((date, index) => {
        const isSelected = currentDate
          ? isEqual(
              format(currentDate, "yyyy-MM-dd"),
              format(date, "yyyy-MM-dd"),
            )
          : isEqual(
              format(selectedDate, "yyyy-MM-dd"),
              format(date, "yyyy-MM-dd"),
            );

        return (
          <Box
            key={"date__selector__" + index}
            border="2px"
            color={!isSelected ? "surface.500" : "primary.500"}
            h={{
              base: "3.5rem",
              md: "3.75rem",
            }}
            w={{
              base: "3.5rem",
              md: "3.75rem",
            }}
            minW={{
              base: "3.5rem",
              md: "3.75rem",
            }}
            rounded="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="sm"
            fontWeight="bold"
            transition="all 0.2s ease-in-out"
            flexDirection="column"
            textTransform="uppercase"
            onClick={() => {
              setSelectedDate(date);
              onDateChange(date);
            }}
            cursor="pointer"
            _hover={{
              color: "primary.500",
              borderColor: "primary.500",
            }}
            {...(borderType === "bottom" && {
              borderBottom: isSelected ? "2px solid" : "none",
              borderBottomColor: isSelected ? "primary.500" : "none",
            })}
            {...dateBox}
            borderColor={
              !isSelected
                ? (dateBox?.borderColor ?? "surface.500")
                : (selectedBorderColor ?? "primary.500")
            }
            backgroundColor={
              !isSelected
                ? (dateBox?.background ?? "transparent")
                : selectedBackgroundColor
            }
          >
            {isToday(date) ? (
              <Text
                color={isSelected ? (selectedTextColor ?? "primary.500") : ""}
              >
                Today
              </Text>
            ) : isTomorrow(date) ? (
              <Text
                color={isSelected ? (selectedTextColor ?? "primary.500") : ""}
              >
                Tomm
              </Text>
            ) : (
              <>
                {timeDisplayType === "single" ? (
                  <Text
                    color={
                      isSelected ? (selectedTextColor ?? "primary.500") : ""
                    }
                  >
                    {`${date.getUTCDate()} ${date.toLocaleString("default", { month: "short" })}`}
                  </Text>
                ) : (
                  <>
                    <Text
                      color={
                        isSelected ? (selectedTextColor ?? "primary.500") : ""
                      }
                    >
                      {date.getUTCDate()}
                    </Text>
                    <Text
                      fontSize="xs"
                      color={
                        isSelected ? (selectedTextColor ?? "primary.500") : ""
                      }
                    >
                      {date.toLocaleString("default", { month: "short" })}
                    </Text>
                  </>
                )}
              </>
            )}
            {showWeekDay && (
              <Text
                fontSize="xs"
                color={isSelected ? (selectedTextColor ?? "primary.500") : ""}
                {...weekDayProps}
              >
                {format(date, "EEE")}
              </Text>
            )}
          </Box>
        );
      })}
    </Flex>
  );
}

export default DateSelector;
